import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useSnackbar } from "notistack";
import { useAiConfig, useAiUsage, useUpdateAiConfig } from "../../hooks/useAiUsage";

interface TenantAiSectionProps {
  tenantId: string;
}

function UsageRow({
  label,
  used,
  limit,
  ratio,
  isDefault,
}: {
  label: string;
  used: number;
  limit: number;
  ratio: number;
  isDefault: boolean;
}) {
  const pct = Math.min(ratio * 100, 100);
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {label}: {used.toLocaleString()} / {limit.toLocaleString()}
          {isDefault ? " (default)" : ""}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {Math.round(ratio * 100)}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={ratio >= 1 ? "error" : ratio >= 0.8 ? "warning" : "primary"}
        sx={{ height: 10, borderRadius: 1 }}
      />
    </Box>
  );
}

/**
 * ai-usage-billing: AI usage (invoicing basis) + quota management per tenant.
 * Usage is aggregated from the metering tables with the same UTC month
 * boundary the quota gate uses. Raising a limit here IS the "expansion pack"
 * switch — takes effect on the tenant's next generation, no deploy needed.
 */
export function TenantAiSection({ tenantId }: TenantAiSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: config } = useAiConfig(tenantId);
  const { data: usage, isLoading } = useAiUsage(tenantId, 6);
  const updateConfig = useUpdateAiConfig(tenantId);

  // Empty field = platform default (stored as null).
  const [tokenLimitInput, setTokenLimitInput] = useState("");
  const [imageLimitInput, setImageLimitInput] = useState("");

  useEffect(() => {
    if (config) {
      setTokenLimitInput(
        config.aiMonthlyTokenLimit === null ? "" : String(config.aiMonthlyTokenLimit),
      );
      setImageLimitInput(
        config.aiMonthlyImageLimit === null ? "" : String(config.aiMonthlyImageLimit),
      );
    }
  }, [config]);

  const parseLimit = (value: string): number | null | undefined => {
    const trimmed = value.trim();
    if (trimmed === "") return null;
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isNaN(parsed) || parsed < 1 ? undefined : parsed;
  };

  const handleSave = () => {
    const tokenLimit = parseLimit(tokenLimitInput);
    const imageLimit = parseLimit(imageLimitInput);
    if (tokenLimit === undefined || imageLimit === undefined) {
      enqueueSnackbar("Limits must be positive integers (or empty for default)", {
        variant: "warning",
      });
      return;
    }
    updateConfig.mutate(
      { aiMonthlyTokenLimit: tokenLimit, aiMonthlyImageLimit: imageLimit },
      {
        onSuccess: () =>
          enqueueSnackbar("AI quota updated — effective immediately", {
            variant: "success",
          }),
        onError: () =>
          enqueueSnackbar("Could not update AI quota", { variant: "error" }),
      },
    );
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <AutoAwesomeIcon color="primary" />
          <Typography variant="h6">AI Usage</Typography>
          {config && (
            <Chip
              label={config.aiBillingMode}
              size="small"
              color={config.aiBillingMode === "TRIAL" ? "warning" : "default"}
              variant="outlined"
            />
          )}
        </Box>

        {isLoading && (
          <Typography variant="body2" color="text.secondary">
            Loading usage…
          </Typography>
        )}

        {usage && (
          <>
            <UsageRow
              label="Tokens (Svenn chat)"
              used={usage.currentMonth.tokens}
              limit={usage.limits.tokenLimit}
              ratio={usage.currentMonth.tokenRatio}
              isDefault={usage.limits.tokenLimitIsDefault}
            />
            <UsageRow
              label="Images (visualization)"
              used={usage.currentMonth.images}
              limit={usage.limits.imageLimit}
              ratio={usage.currentMonth.imageRatio}
              isDefault={usage.limits.imageLimitIsDefault}
            />

            {usage.environmentsIncluded.length === 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No environment databases reachable — usage may be incomplete.
              </Alert>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Environments included: {usage.environmentsIncluded.join(", ")}
              </Typography>
            )}

            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Tokens</TableCell>
                  <TableCell align="right">Images</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usage.months.map((m) => (
                  <TableRow key={m.month}>
                    <TableCell>{m.month}</TableCell>
                    <TableCell align="right">{m.tokens.toLocaleString()}</TableCell>
                    <TableCell align="right">{m.images.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Monthly quotas (empty = platform default)
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            label="Token limit"
            size="small"
            value={tokenLimitInput}
            onChange={(e) => setTokenLimitInput(e.target.value)}
            slotProps={{ htmlInput: { inputMode: "numeric" } }}
            sx={{ width: 160 }}
          />
          <TextField
            label="Image limit"
            size="small"
            value={imageLimitInput}
            onChange={(e) => setImageLimitInput(e.target.value)}
            slotProps={{ htmlInput: { inputMode: "numeric" } }}
            sx={{ width: 160 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={updateConfig.isPending}
          >
            Save quotas
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Raising a limit is the expansion-pack switch — it takes effect on the
          tenant's next generation. Invoicing stays manual (Tripletex).
        </Typography>
      </CardContent>
    </Card>
  );
}
